import { isAxiosError } from "axios";

/**
 * C4 error-code i18n (RC-4). Screens never render the server's English `error.message`;
 * they render `apiErrorText(err, t, fallbackKey)`, which maps the API's `error.code` to the
 * `errors.<CODE>` strings in messages/{en,hi}.json.
 *
 *   const t = useTranslations();               // root translator: keys are full paths
 *   setError(apiErrorText(err, t, "auth.registrationFailed"));
 *
 * `err` may be an axios error, a thrown `ApiError` (see `readApiErrorResponse` for apiFetch),
 * a raw `{ error: { code, message } }` body, or a fetch `TypeError` (network).
 */

/** Codes with a shared translation under `errors.*` (plus `errors.generic`). */
export const API_ERROR_CODES = [
  "VALIDATION",
  "FILE_TOO_LARGE",
  "UNAUTHORIZED",
  "FORBIDDEN",
  "NOT_FOUND",
  "RATE_LIMITED",
  "NETWORK",
  "INTERNAL_ERROR",
  "SESSION_EXPIRED",
  // MT-1 Plan 4 S12: a school's Bunny Stream / Storage (FR-14, AC-62).
  "STORAGE_NOT_AVAILABLE",
  "VIDEO_STORAGE_NOT_CONFIGURED",
  "FILE_STORAGE_NOT_CONFIGURED",
  "STORAGE_NOT_CONFIGURED",
  "STORAGE_CONFIG_CHANGED",
  "STORAGE_IN_USE",
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

/** Older or synonymous server codes folded onto a shared translation. */
const CODE_ALIASES: Readonly<Record<string, ApiErrorCode>> = {
  VALIDATION_ERROR: "VALIDATION",
  INVALID_INPUT: "VALIDATION",
  PAYLOAD_TOO_LARGE: "FILE_TOO_LARGE",
  UNAUTHENTICATED: "UNAUTHORIZED",
};

/** MT-1 S13c: one refused field of a `VALIDATION_ERROR` the server can name (e.g. a secret to re-enter). */
export type ApiErrorIssue = { path: string; code: string };

export type ParsedApiError = {
  code: string;
  message: string;
  status: number | null;
  fields?: Record<string, string>;
  /** MT-1 S13c: per-field refusals (`error.issues`), e.g. the payment card's "enter it again". */
  issues?: ApiErrorIssue[];
  /** MT-1 S13c: when a 429 may be retried, from the body (cross-origin) or a readable header. */
  retryAfterSeconds?: number;
};

/** Thrown by `readApiErrorResponse` so apiFetch callers get the same shape as axios callers. */
export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number | null,
    public readonly fields?: Record<string, string>,
    public readonly issues?: ApiErrorIssue[],
    public readonly retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type ErrorEnvelope = {
  error?: {
    code?: unknown;
    message?: unknown;
    fields?: unknown;
    issues?: unknown;
    retryAfterSeconds?: unknown;
  };
};

function readIssues(raw: unknown): ApiErrorIssue[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const issues = raw.flatMap((item) =>
    item &&
    typeof item === "object" &&
    typeof (item as { path?: unknown }).path === "string" &&
    typeof (item as { code?: unknown }).code === "string"
      ? [{ path: (item as ApiErrorIssue).path, code: (item as ApiErrorIssue).code }]
      : [],
  );
  return issues.length > 0 ? issues : undefined;
}

function readSeconds(raw: unknown): number | undefined {
  return typeof raw === "number" && Number.isFinite(raw) && raw >= 0 ? raw : undefined;
}

function codeFromStatus(status: number | null): string | null {
  if (status === null) return null;
  if (status === 401) return "UNAUTHORIZED";
  if (status === 403) return "FORBIDDEN";
  if (status === 404) return "NOT_FOUND";
  if (status === 413) return "FILE_TOO_LARGE";
  if (status === 429) return "RATE_LIMITED";
  if (status === 400 || status === 422) return "VALIDATION";
  if (status >= 500) return "INTERNAL_ERROR";
  return null;
}

function fromBody(body: unknown, status: number | null): ParsedApiError | null {
  const envelope = (body && typeof body === "object" ? body : {}) as ErrorEnvelope;
  const inner = envelope.error;
  const code =
    (inner && typeof inner.code === "string" && inner.code) || codeFromStatus(status) || null;
  if (!code) return null;
  const parsed: ParsedApiError = {
    code,
    message: inner && typeof inner.message === "string" ? inner.message : "",
    status,
  };
  if (inner?.fields && typeof inner.fields === "object") {
    parsed.fields = inner.fields as Record<string, string>;
  }
  const issues = readIssues(inner?.issues);
  if (issues) parsed.issues = issues;
  const retryAfterSeconds = readSeconds(inner?.retryAfterSeconds);
  if (retryAfterSeconds !== undefined) parsed.retryAfterSeconds = retryAfterSeconds;
  return parsed;
}

/** Normalise anything a request can throw into `{ code, message, status, fields }`, or null. */
export function readApiError(err: unknown): ParsedApiError | null {
  if (err instanceof ApiError) {
    return {
      code: err.code,
      message: err.message,
      status: err.status,
      fields: err.fields,
      issues: err.issues,
      retryAfterSeconds: err.retryAfterSeconds,
    };
  }
  if (isAxiosError(err)) {
    if (!err.response) {
      return { code: "NETWORK", message: err.message, status: null };
    }
    return fromBody(err.response.data, err.response.status);
  }
  // fetch() rejects with a TypeError when the request never reached a server.
  if (err instanceof TypeError) return { code: "NETWORK", message: err.message, status: null };
  if (err instanceof Error) return null;
  return fromBody(err, null);
}

/** For apiFetch callers: `if (!res.ok) throw await readApiErrorResponse(res);` */
export async function readApiErrorResponse(res: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  const parsed = fromBody(body, res.status) ?? {
    code: "INTERNAL_ERROR",
    message: "",
    status: res.status,
  };
  // The body carries the retry time: production is split-origin and CORS exposes no headers, so
  // `Retry-After` is readable only same-origin (dev) — a fallback, never the source.
  const header = res.headers.get("Retry-After");
  const headerSeconds = header !== null && /^\d+$/.test(header.trim()) ? Number(header) : undefined;
  return new ApiError(
    parsed.code,
    parsed.message,
    res.status,
    parsed.fields,
    parsed.issues,
    parsed.retryAfterSeconds ?? headerSeconds,
  );
}

/**
 * The shape of next-intl's translator this helper needs. Declared with method syntax so a
 * translator typed with literal message keys (useTranslations / createTranslator) still fits.
 */
type TranslateFn = { bivariant(key: string): string }["bivariant"];
export type ErrorTranslator = TranslateFn & {
  has(key: string): boolean;
};

export type ApiErrorTextOptions = {
  /** For VALIDATION errors: field name → message key, e.g. `{ password: "auth.passwordHintLong" }`. */
  fields?: Record<string, string>;
  /**
   * A caller namespace checked first for `<namespace>.<CODE>`, e.g. `"checkout.errors"`; a list is
   * checked in order (the screen's own wording first, then its section's).
   */
  namespace?: string | readonly string[];
};

/**
 * Translated, user-facing text for a failed request. Order: field-specific key (VALIDATION) →
 * `<namespace>.<CODE>` → `errors.<CODE>` (with aliases) → `fallbackKey` → `errors.generic`.
 * The server's English `message` is never returned.
 */
export function apiErrorText(
  err: unknown,
  t: ErrorTranslator,
  fallbackKey?: string,
  options: ApiErrorTextOptions = {},
): string {
  const parsed = readApiError(err);
  // A message that needs a value (e.g. `{minutes}`) cannot be formatted here: next-intl then
  // returns the key path itself, which must never reach the screen (S9 rehearsal) — try the next.
  const tryKey = (key: string | undefined): string | null => {
    if (!key || !t.has(key)) return null;
    const text = t(key);
    return text === key || text.endsWith(`.${key}`) ? null : text;
  };

  if (parsed) {
    const shared = CODE_ALIASES[parsed.code] ?? parsed.code;
    if (shared === "VALIDATION" && parsed.fields && options.fields) {
      for (const field of Object.keys(parsed.fields)) {
        const text = tryKey(options.fields[field]);
        if (text) return text;
      }
    }
    const namespaces =
      options.namespace === undefined
        ? []
        : typeof options.namespace === "string"
          ? [options.namespace]
          : options.namespace;
    let text: string | null = null;
    for (const ns of namespaces) {
      text = tryKey(`${ns}.${parsed.code}`);
      if (text) break;
    }
    text ??= tryKey(`errors.${shared}`);
    if (text) return text;
  }
  return tryKey(fallbackKey) ?? t("errors.generic");
}
